import {
  DashboardOverviewTasksQueryVariables,
  FilterGroupingEnum,
  FormTemplateQuestionFieldOptionsValidityHintEnum,
  Task,
  TaskFilterInputObject,
  TaskScopeNameEnum,
  useDashboardTimelineTasksQuery,
} from 'graphql-common';
import _get from 'lodash/get';
import { Option } from '@lib/interfaces/form';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters from '@lib/utils/createSearchFilters';
import getTaskCounterData from '@lib/utils/getTaskCounterData';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { SelectOption } from '@lib/components/Select/types';
import { ListQueryHookProps } from '../types';

export default function useDashboardTimelineTasksQueryHook({
  filterParams,
  paginationParams,
  searchQuery,
  skip,
  scopeName,
}: ListQueryHookProps) {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );
  const period = _get(filterParams, TasksFilterFieldNames.Period) as string[];
  const periodStart = period?.[0];
  const periodEnd = period?.[1];
  const sites = _get(filterParams, TasksFilterFieldNames.Site) as Option[];
  const formCategories = _get(
    filterParams,
    TasksFilterFieldNames.FormCategory,
  ) as Option[];
  const validityHints = _get(
    filterParams,
    TasksFilterFieldNames.ValidityHints,
  ) as SelectOption[];
  const tasksQueryVariables: DashboardOverviewTasksQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    scope: { name: scopeName as TaskScopeNameEnum },
  };
  const filters: TaskFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'name' },
        { fieldName: 'formCategoryName' },
      ]),
    );
  }
  if (periodStart && periodEnd) {
    filters.push({
      completedAt: {
        grouping: FilterGroupingEnum.And,
        predicate: { gteq: periodStart },
        filterGroup: 'completedAt',
      },
    });
    filters.push({
      completedAt: {
        grouping: FilterGroupingEnum.And,
        predicate: { lteq: periodEnd },
        filterGroup: 'completedAt',
      },
    });
  }
  if (sites?.length) {
    filters.push({
      siteId: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: sites.map(({ value }) => value as string) },
      },
    });
  }
  if (formCategories?.length) {
    filters.push({
      formCategoryId: {
        grouping: FilterGroupingEnum.And,
        predicate: { in: formCategories.map(({ value }) => value as string) },
      },
    });
  }
  if (validityHints?.length) {
    filters.push({
      completionValidityHint: {
        grouping: FilterGroupingEnum.And,
        predicate: {
          in: validityHints.map(
            ({ value }) =>
              value as FormTemplateQuestionFieldOptionsValidityHintEnum,
          ),
        },
      },
    });
  }
  if (filters.length) {
    tasksQueryVariables.filters = filters;
  }
  if (orderingField && orderingDirection) {
    tasksQueryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const tasksQuery = useDashboardTimelineTasksQuery({
    fetchPolicy: 'no-cache',
    variables: tasksQueryVariables,
    skip,
  });
  const {
    loading: tasksLoading,
    data,
    previousData,
    refetch: refetchTasks,
    error: tasksError,
  } = tasksQuery;
  const queryData = data || previousData;
  const { tasks, taskIds, ...taskCounterData } = queryData || {};
  const tasksFirstLoading = tasksLoading && previousData === undefined;
  const metadata = _get(queryData, 'tasks.metadata');
  const totalTasksCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);
  return {
    refetchTasks,
    taskCounterData: getTaskCounterData(taskCounterData),
    taskIds: taskIds?.collection.map(({ id }) => id),
    tasks: _get(tasks, 'collection', []) as Task[],
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    totalPages,
    totalTasksCount,
  };
}
